import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PARTNERS, ANIMATION_VARIANTS, HERO_CONTENT } from '../../constants';
import { 
  useOptimizedBackground,
  usePerformanceMonitoring
} from '../../hooks/useAnimations';
import { Marquee } from '../animations';
import { handlePageNavigation } from '../../utils/navigation';

const PartnersSection: React.FC = () => {
  usePerformanceMonitoring('PartnersSection');
  
  // Use background optimization hook
  const { backgroundClasses, backgroundStyle } = useOptimizedBackground(
    HERO_CONTENT.backgroundImage,
    'linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.8))'
  );

  // Preload partner logos
  useEffect(() => {
    PARTNERS.forEach(partner => {
      const img = new Image();
      img.src = partner.logo;
    });
  }, []);

  // Memoized partner elements for better performance
  const partnerElements = useMemo(() => 
    PARTNERS.map((partner) => (
      <motion.div
        key={partner.name}
        variants={ANIMATION_VARIANTS.slideUp}
        className="flex-shrink-0 mx-8 text-center group"
        whileHover={{ scale: 1.05 }}
        transition={{ duration: 0.3 }}
      >
        <div className="w-32 h-20 mx-auto mb-4 bg-partner-card-gradient backdrop-blur-sm rounded-lg p-4 flex items-center justify-center transition-all duration-300 group-hover:bg-white/30 group-hover:shadow-lg will-change-transform transform-gpu">
          <img
            src={partner.logo}
            alt={partner.name}
            className="max-w-full max-h-full object-contain transition-all duration-300 group-hover:scale-110 will-change-transform transform-gpu"
            loading="lazy"
            decoding="async"
          />
        </div>
        <h4 className="text-white font-medium text-sm">{partner.name}</h4>
        <p className="text-gray-400 text-xs">{partner.description}</p>
      </motion.div>
    )), []);

  return (
    <section className="relative section-padding bg-secondary-900" id="partners">
      {/* Optimized background image with rotation */}
      <div 
        className={backgroundClasses}
        style={{
          ...backgroundStyle,
          transform: 'translateZ(0) rotate(180deg)',
          backgroundPosition: 'bottom',
        }}
      />
      
      {/* Content Container */}
      <div className="container relative z-40">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={ANIMATION_VARIANTS.staggerChildren}
        >
          {/* Section Header */}
          <motion.div 
            variants={ANIMATION_VARIANTS.slideUp} 
            className="text-center mb-16"
          >
            <h2 className="heading-1 mb-6">
              Our <span className='text-primary-gradient'>Partners</span>
            </h2>
            <p className="body-text max-w-2xl mx-auto">
              Collaboration with industry-leading companies to deliver cutting-edge AI and cloud solutions
            </p>
          </motion.div>

          {/* Partners Marquee */}
          <motion.div 
            variants={ANIMATION_VARIANTS.slideUp}
            className="relative"
          >
            <Marquee speed="slow" expandOnHover={true} className="py-8">
              <div className="flex">
                {partnerElements}
              </div>
            </Marquee>
          </motion.div>

          {/* Call to Action */}
          <motion.div
            variants={ANIMATION_VARIANTS.slideUp}
            className="text-center mt-16"
          >
            <p className="text-gray-300 mb-6">
              Interested in partnering with us?
            </p>
            <Link
              to="/contact"
              className="btn-futuristic"
              onClick={handlePageNavigation}
            >
              Become a Partner
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default React.memo(PartnersSection);
